import { Box, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import _ from 'lodash';

import { convertDateTimeIntoTimezone } from '../../../utils/util';
import { MMDDYYYYHMM_DATE_FORMAT_24_HRS } from '../../../utils/constants';
import { Action, FLOOR_DATA_LAG_MINUTES, quotaWindowHistory, FLOOR_RECOVERY_DEADBAND_PERCENT, formatMinutes, formatWindow, Quota, QuotaWindow, suppressionReasonLabel, windowLengthSeconds } from '../types';
import { floorBytes, floorTiers, floorWindowRange } from './FloorUsageBar';

const COLORS = {
    ok: '#3AA57A',
    near: '#7FBFA3',
    alert: '#FFCC00',
    exceed: '#EE3939',
    skipped: '#C9CCD3',
    floor: '#16664A',
    tier: '#9A8A3A',
};

// the most severe tier a window fell under decides its colour
const windowColor = (window: QuotaWindow, limit: number, tierPercents: number[]): string => {
    if (window.reason) return COLORS.skipped;
    const percent = limit > 0 ? window.bytes / limit * 100 : 0;
    if (percent >= 100) return COLORS.ok;
    const breached = tierPercents.filter(p => percent <= p);
    if (breached.length === 0) return COLORS.near;
    return Math.min(...breached) === Math.min(...tierPercents) ? COLORS.exceed : COLORS.alert;
}

const tierCell = (tier: Action, userTimezone: string): string => {
    if (tier.executed) {
        return tier.executed_at ? `fired ${convertDateTimeIntoTimezone(tier.executed_at, userTimezone, MMDDYYYYHMM_DATE_FORMAT_24_HRS)}` : 'fired';
    }
    return `${tier.breach_window_count ?? 0} of ${tier.consecutive_windows ?? 1}`;
}

interface FloorUsageTooltipProps {
    quota: Quota;
    userTimezone: string;
}

export function FloorUsageTooltip(props: FloorUsageTooltipProps) {
    const { quota, userTimezone } = props;

    const limit = Number(quota.limit);
    const used = Number(quota.current_usage_updated ?? quota.current_usage ?? 0);
    const suppressed = quota.status === 'unknown';
    const windowMinutes = windowLengthSeconds(quota) / 60;
    const tiers = floorTiers(quota);
    const tierPercents = _.uniq(tiers.map(t => t.usage_percentage));
    const history = quotaWindowHistory(quota);

    // the end timestamps are authoritative: the newest window trails the clock by the data lag
    const categories = history.map(w => convertDateTimeIntoTimezone(w.end, userTimezone, 'HH:mm'));
    const useGB = limit >= 1000 * 1000 * 1000;
    const unitDivisor = useGB ? 1000 * 1000 * 1000 : 1000 * 1000;
    const unit = useGB ? 'GB' : 'MB';
    const scaled = (bytes: number) => Math.round(bytes / unitDivisor * 100) / 100;

    const options: Highcharts.Options = {
        chart: { type: 'column', height: 200, width: 640, backgroundColor: 'transparent', spacing: [8, 8, 4, 4] },
        title: { text: undefined },
        credits: { enabled: false },
        legend: { enabled: false },
        exporting: { enabled: false },
        xAxis: {
            categories,
            labels: { style: { fontSize: '10px', color: '#777' } },
            plotBands: history.map((w, i) => w.reason ? { from: i - 0.5, to: i + 0.5, color: '#F2F3F5' } : null).filter(Boolean) as Highcharts.XAxisPlotBandsOptions[],
        },
        yAxis: {
            title: { text: unit, style: { fontSize: '10px' } },
            min: 0,
            max: Math.max(scaled(limit) * 1.4, ...history.map(w => scaled(w.bytes))),
            gridLineWidth: 0,
            labels: { style: { fontSize: '10px', color: '#777' } },
            plotLines: [
                { value: scaled(limit), color: COLORS.floor, width: 2, zIndex: 5, label: { text: 'Minimum', align: 'right', style: { color: COLORS.floor, fontSize: '10px', fontWeight: '600' } } },
                ...tierPercents.filter(p => p > 0 && p < 100).map(p => ({
                    value: scaled(limit * p / 100), color: COLORS.tier, width: 1, dashStyle: 'Dash' as Highcharts.DashStyleValue, zIndex: 5,
                    label: { text: `${p}%`, align: 'left' as Highcharts.AlignValue, x: -2, style: { color: COLORS.tier, fontSize: '9px' } },
                })),
            ],
        },
        tooltip: {
            outside: true,
            formatter: function () {
                const w = history[this.point.index];
                const reason = w?.reason ? ` · not evaluated: ${suppressionReasonLabel(w.reason)}` : w?.withheld ? ' · actions withheld, link moving again' : '';
                return `Window ending ${this.x}<br/><b>${floorBytes(w?.bytes ?? 0)}</b>${reason}`;
            },
        },
        plotOptions: { column: { pointPadding: 0.08, groupPadding: 0.06, borderWidth: 0, animation: false } },
        series: [{
            type: 'column',
            name: 'Window usage',
            data: history.map(w => ({ y: scaled(w.bytes), color: windowColor(w, limit, tierPercents) })),
        }],
    };

    return (
        <Paper elevation={3}>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell>DATA SOURCE</TableCell>
                        <TableCell>LAST WINDOW</TableCell>
                        <TableCell>MINIMUM</TableCell>
                        {tiers.map(t => <TableCell key={t.id}>≤{t.usage_percentage}% · {windowMinutes > 0 ? formatMinutes((t.consecutive_windows ?? 1) * windowMinutes) : `${t.consecutive_windows ?? 1} windows`}</TableCell>)}
                        <TableCell>WINDOW</TableCell>
                        <TableCell>RECOVERY</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableCell>
                            <Stack spacing={0.5} alignItems='flex-start'>
                                <Box component='span'><Typography variant='caption' color='text.secondary'>WAN INTERFACES: </Typography>{quota.wan_links?.join(', ')}</Box>
                            </Stack>
                        </TableCell>
                        <TableCell>
                            {suppressed
                                ? `Not evaluated: ${suppressionReasonLabel(quota.suppression_reason)}`
                                : `${floorBytes(used)} (${limit > 0 ? Math.round(used / limit * 100) : 0}%)`}
                        </TableCell>
                        <TableCell>{floorBytes(limit)}</TableCell>
                        {tiers.map(t => <TableCell key={t.id}
                            className={t.executed ? 'quota_management--usage_tooltip--cell_exceeded' : (t.breach_window_count ?? 0) > 0 ? 'quota_management--usage_tooltip--cell_alerting' : ''}>
                            {tierCell(t, userTimezone)}
                        </TableCell>)}
                        <TableCell>
                            {formatWindow(quota.period, quota.period_unit)} rolling
                            <div><Typography variant='caption' color='text.secondary'>{floorWindowRange(quota, userTimezone)}</Typography></div>
                        </TableCell>
                        <TableCell>{quota.recovery_windows ?? 2} windows ≥ {FLOOR_RECOVERY_DEADBAND_PERCENT}%</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <Box className='floor_usage--history'>
                <Typography className='floor_usage--history_title'>
                    Last {history.length} window{history.length === 1 ? '' : 's'} ({formatWindow(quota.period, quota.period_unit)} each)
                </Typography>
                {history.length > 0
                    ? <HighchartsReact highcharts={Highcharts} options={options} />
                    : <Typography className='floor_usage--history_empty'>No completed windows yet.</Typography>}
                <Typography className='floor_usage--history_note'>
                    Each window is judged about {FLOOR_DATA_LAG_MINUTES} minutes after it closes, once its data has arrived. Grey = not evaluated, which resets the count of windows in a row.
                </Typography>
            </Box>
        </Paper>
    );
}
